import XLSXStyle from 'xlsx-js-style'
import moment from 'moment'
import {
  getTicketLocationFull,
  getTicketAvailableTime,
  getTicketReporterAffiliation,
  getTicketUserName,
  getTicketMaintenanceImage
} from '@/utils/ticketViewHelper'
import { translateDepartment, translateSection } from '@/utils/categoryi18n'

const XLSX = XLSXStyle.default || XLSXStyle

const getApiUrl = () => (process.env.VUE_APP_APP_URL || '').replace(/\/$/, '')

/**
 * Palette Tokens for SmartFix MFU Executive Excel Theme
 */
const COLORS = {
  NAVY_PRIMARY: '1E3A8A',     // Deep Navy Brand
  NAVY_SUBHEADER: '1E40AF',   // Accent Navy
  TEAL_HEADER: '0F766E',      // Deep Teal for Performance Summary
  ICE_BLUE: 'EFF6FF',         // Soft Blue Total Bar
  MINT_LIGHT: 'F0FDF4',       // Soft Mint Summary Bar
  SLATE_50: 'F8FAFC',         // Subtle Zebra Row Fill
  SLATE_100: 'F1F5F9',        // Metadata Row Fill
  SLATE_700: '334155',        // Metadata Dark Text
  SLATE_900: '0F172A',        // Standard Dark Text
  BORDER_HEADER: '94A3B8',    // Header Border
  BORDER_DATA: 'E2E8F0',      // Data Cell Border
  BORDER_TOTAL: '1E3A8A',     // Total Row Top Border
  BORDER_TOTAL_TEAL: '0F766E',// Teal Total Row Top Border
  LINK_BLUE: '2563EB',        // Hyperlink Blue
  GREEN_SLA: '16A34A',        // SLA Met >= 80%
  AMBER_SLA: 'D97706',        // SLA Met < 80%
  WHITE: 'FFFFFF'
}

/**
 * Generate a beautifully styled, executive-grade Excel Report for Admin and Supervisor
 * Contains 2 worksheets:
 *   1. รายงานการแจ้งซ่อม (Detailed Tickets with 15 columns & photo links)
 *   2. สรุปผลการประเมินช่าง (Technician Performance, SLA %, Rating & Cost)
 */
export function exportSmartFixExcelReport({
  validTickets = [],
  evalItems = [],
  startDate = '',
  endDate = '',
  departmentName = 'all',
  t = key => key,
  localeLang = 'th',
  formatDate = d => (d ? moment(d).format('DD/MM/YYYY HH:mm') : '-')
}) {
  const wb = XLSX.utils.book_new()
  const today = moment().format('YYYY-MM-DD')
  const isEn = localeLang === 'en'
  const printTimestamp = isEn
    ? moment().format('DD/MM/YYYY HH:mm')
    : moment().format('DD/MM/YYYY HH:mm น.')

  // Date range description text
  let dateRangeText = isEn ? 'All Records' : 'ข้อมูลทั้งหมด (All Records)'
  if (startDate && endDate) {
    const s = moment(startDate).format('DD/MM/YYYY')
    const e = moment(endDate).format('DD/MM/YYYY')
    dateRangeText = isEn ? `${s} to ${e}` : `${s} ถึง ${e}`
  } else if (startDate) {
    dateRangeText = isEn ? `Since ${moment(startDate).format('DD/MM/YYYY')}` : `ตั้งแต่ ${moment(startDate).format('DD/MM/YYYY')}`
  } else if (endDate) {
    dateRangeText = isEn ? `Until ${moment(endDate).format('DD/MM/YYYY')}` : `จนถึง ${moment(endDate).format('DD/MM/YYYY')}`
  }

  const deptText = departmentName === 'all'
    ? (isEn ? 'All Departments' : 'ทุกแผนก (ทั้งหมด)')
    : (isEn ? (translateDepartment(departmentName, 'en') || translateSection(departmentName, 'en') || departmentName) : departmentName)

  // -------------------------------------------------------------
  // 1. SHEET 1: รายงานการแจ้งซ่อม (Detailed Tickets)
  // -------------------------------------------------------------
  const sheet1Headers = [
    'ลำดับ',
    'วันที่แจ้ง',
    'รายการ',
    'อาคาร',
    'ผู้แจ้ง',
    'หน่วยงานที่แจ้ง',
    'เลขที่แจ้ง',
    'วันที่สะดวกเข้าดำเนินการ',
    'ผู้รับมอบหมายงาน',
    'กำหนดวันที่เสร็จ',
    'แก้ไข',
    'ระยะเวลาดำเนินการจริง',
    'อะไหล่',
    'ค่าใช้จ่าย (บาท)',
    'รายละเอียด'
  ]

  const sheet1Data = []

  // Row 0: Organization Banner
  sheet1Data.push(['มหาวิทยาลัยแม่ฟ้าหลวง - MFU SmartFix System', ...new Array(14).fill('')])
  // Row 1: Report Title
  sheet1Data.push(['รายงานประวัติและรายละเอียดการแจ้งซ่อมบำรุง (Maintenance Tickets Report)', ...new Array(14).fill('')])
  // Row 2: Metadata Info
  sheet1Data.push([
    isEn
      ? `Date Range: ${dateRangeText}   |   Department/Division: ${deptText}   |   Exported: ${printTimestamp}   |   Total: ${validTickets.length} records`
      : `ช่วงวันที่: ${dateRangeText}   |   แผนก/ฝ่าย: ${deptText}   |   ออกรายงานเมื่อ: ${printTimestamp}   |   จำนวนทั้งหมด: ${validTickets.length} รายการ`,
    ...new Array(14).fill('')
  ])
  // Row 3: Blank Spacer
  sheet1Data.push(new Array(15).fill(''))
  // Row 4: Column Headers
  sheet1Data.push(sheet1Headers)

  let totalTicketsCost = 0

  // Rows 5..N: Data Rows
  validTickets.forEach((tkt, idx) => {
    const indexNo = idx + 1
    const dateInformedStr = formatDate(tkt.userPart?.dateTimeInformed || tkt.createdAt)
    const categoryStr = tkt.userPart?.subCategory?.name || tkt.userPart?.subCategory || '-'
    const locationStr = getTicketLocationFull(tkt, t, localeLang)

    const userHelperName = getTicketUserName(tkt)
    const reporterName = userHelperName !== '-' ? userHelperName : (tkt.userPart?.fullname || tkt.userPart?.username || '-')
    const reporterAffiliation = getTicketReporterAffiliation(tkt)
    const ticketNo = tkt.ticketNumber || (tkt.id || tkt._id || '').substring(0, 8).toUpperCase()
    const availableTimeStr = getTicketAvailableTime(tkt, 'th', t)

    // Technician assignments
    let techName = '-'
    const assignments = tkt?.supervisorPart?.assignments
    if (Array.isArray(assignments) && assignments.length > 0) {
      const names = assignments
        .filter(a => a && a.status !== 'rejected')
        .map(a => {
          const techObj = typeof a.assignee === 'object' ? a.assignee : null
          if (techObj) {
            const n = `${techObj.name || ''} ${techObj.surname || ''}`.trim()
            if (n) return n
            if (techObj.username) return techObj.username
          }
          return null
        })
        .filter(Boolean)
      if (names.length > 0) techName = names.join(', ')
    }
    if (techName === '-') {
      const legacy = tkt.supervisorPart?.assignedTechnician
      if (legacy && typeof legacy === 'object') {
        techName = `${legacy.name || ''} ${legacy.surname || ''}`.trim() || legacy.username || '-'
      }
    }

    const slaDeadlineStr = tkt.slaDeadline ? formatDate(tkt.slaDeadline) : '-'

    // Maintenance image link
    let maintImageStr = '-'
    const rawImg = getTicketMaintenanceImage(tkt)
    if (rawImg) {
      if (rawImg.startsWith('http://') || rawImg.startsWith('https://')) {
        maintImageStr = rawImg
      } else {
        const baseUrl = getApiUrl()
        maintImageStr = `${baseUrl}/uploads/${rawImg}`
      }
    }

    // Actual duration
    let actualDurationStr = '-'
    const startTime = tkt.technicianPart?.dateTimeStarted || tkt.technicianPart?.dateTimeAccepted || tkt.userPart?.dateTimeInformed || tkt.createdAt
    const endTime = tkt.technicianPart?.dateTimeFinished || (['done', 'welldone'].includes(tkt.status) ? tkt.updatedAt : null)
    if (startTime && endTime) {
      const diffMs = new Date(endTime).getTime() - new Date(startTime).getTime()
      if (diffMs > 0) {
        const diffMins = Math.floor(diffMs / (1000 * 60))
        const hours = Math.floor(diffMins / 60)
        const mins = diffMins % 60
        const days = Math.floor(hours / 24)
        const remHours = hours % 24
        if (days > 0) {
          actualDurationStr = `${days} วัน ${remHours} ชม. ${mins} นาที`
        } else if (hours > 0) {
          actualDurationStr = `${hours} ชม. ${mins} นาที`
        } else {
          actualDurationStr = `${mins} นาที`
        }
      }
    } else if (['in_progress', 'assigned'].includes(tkt.status)) {
      actualDurationStr = 'กำลังดำเนินการ'
    }

    // Spare parts
    const techPart = tkt.technicianPart || {}
    let sparePartsStr = '-'
    if (Array.isArray(techPart.parts) && techPart.parts.length) {
      sparePartsStr = techPart.parts.map(p => `${p.name || '-'} (${p.quantity || 1} ชิ้น)`).join(', ')
    } else if (techPart.spareParts && techPart.spareParts !== '-') {
      sparePartsStr = `${techPart.spareParts} (${techPart.quantity || 1} ชิ้น)`
    } else if (techPart.noPartsUsed) {
      sparePartsStr = 'ไม่ได้ใช้อะไหล่'
    }

    // Cost calculation
    let ticketCost = 0
    if (Array.isArray(techPart.parts) && techPart.parts.length) {
      ticketCost = techPart.parts.reduce((sum, p) => sum + (Number(p.unitPrice) || 0) * (Number(p.quantity) || 1), 0)
    } else if (Number(techPart.cost) > 0) {
      ticketCost = Number(techPart.cost)
    }
    totalTicketsCost += ticketCost

    const detailsStr = techPart.maintenanceDetails || tkt.userPart?.problemDetails || '-'

    const maintCell = maintImageStr !== '-'
      ? {
          v: 'คลิกดูรูปภาพ',
          t: 's',
          l: { Target: maintImageStr, Tooltip: 'คลิกเพื่อดูรูปภาพผลการซ่อม' },
          f: `HYPERLINK("${maintImageStr}", "คลิกดูรูปภาพ")`
        }
      : '-'

    sheet1Data.push([
      indexNo,
      dateInformedStr,
      categoryStr,
      locationStr,
      reporterName,
      reporterAffiliation,
      ticketNo,
      availableTimeStr,
      techName,
      slaDeadlineStr,
      maintCell,
      actualDurationStr,
      sparePartsStr,
      ticketCost > 0 ? ticketCost.toLocaleString('th-TH') : '0',
      detailsStr
    ])
  })

  // Summary Row at the bottom
  const summaryRowIndex1 = sheet1Data.length
  sheet1Data.push([
    `ยอดรวมค่าใช้จ่ายทั้งหมด (${validTickets.length} รายการ)`,
    ...new Array(12).fill(''),
    `${totalTicketsCost.toLocaleString('th-TH')} บาท`,
    ''
  ])

  const ws1 = XLSX.utils.aoa_to_sheet(sheet1Data)

  // Merged ranges for Sheet 1
  ws1['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 14 } }, // Main Title
    { s: { r: 1, c: 0 }, e: { r: 1, c: 14 } }, // Subtitle
    { s: { r: 2, c: 0 }, e: { r: 2, c: 14 } }, // Metadata info
    { s: { r: summaryRowIndex1, c: 0 }, e: { r: summaryRowIndex1, c: 12 } } // Summary title
  ]

  // Column widths for Sheet 1
  ws1['!cols'] = [
    { wch: 8 },  // ลำดับ
    { wch: 18 }, // วันที่แจ้ง
    { wch: 25 }, // รายการ
    { wch: 28 }, // อาคาร
    { wch: 22 }, // ผู้แจ้ง
    { wch: 26 }, // หน่วยงานที่แจ้ง
    { wch: 18 }, // เลขที่แจ้ง
    { wch: 24 }, // วันที่สะดวกเข้าดำเนินการ
    { wch: 26 }, // ผู้รับมอบหมายงาน
    { wch: 18 }, // กำหนดวันที่เสร็จ
    { wch: 16 }, // แก้ไข (ลิงก์รูปภาพ)
    { wch: 22 }, // ระยะเวลาดำเนินการจริง
    { wch: 28 }, // อะไหล่
    { wch: 18 }, // ค่าใช้จ่าย
    { wch: 38 }  // รายละเอียด
  ]

  // Row heights for Sheet 1
  const rowHeights1 = [
    { hpt: 32 }, // Title
    { hpt: 24 }, // Subtitle
    { hpt: 22 }, // Metadata
    { hpt: 8 },  // Spacer
    { hpt: 30 }  // Table Headers
  ]
  for (let i = 0; i < validTickets.length; i++) {
    rowHeights1.push({ hpt: 24 })
  }
  rowHeights1.push({ hpt: 28 }) // Summary Row
  ws1['!rows'] = rowHeights1

  // Freeze pane below Table Header (Row 5, 0-indexed)
  ws1['!views'] = [{ xSplit: 0, ySplit: 5, topLeftCell: 'A6', activeCell: 'A6', state: 'frozen' }]

  // -------------------------------------------------------------
  // Apply Cell Styling for Sheet 1
  // -------------------------------------------------------------
  // Title Cell (A1)
  ws1['A1'].s = {
    font: { sz: 14, bold: true, color: { rgb: COLORS.WHITE }, name: 'Calibri' },
    fill: { fgColor: { rgb: COLORS.NAVY_PRIMARY } },
    alignment: { horizontal: 'center', vertical: 'center' }
  }

  // Subtitle Cell (A2)
  ws1['A2'].s = {
    font: { sz: 11, bold: true, color: { rgb: COLORS.WHITE }, name: 'Calibri' },
    fill: { fgColor: { rgb: COLORS.NAVY_SUBHEADER } },
    alignment: { horizontal: 'center', vertical: 'center' }
  }

  // Metadata Cell (A3)
  ws1['A3'].s = {
    font: { sz: 10, bold: false, color: { rgb: COLORS.SLATE_700 }, name: 'Calibri' },
    fill: { fgColor: { rgb: COLORS.SLATE_100 } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: {
      bottom: { style: 'thin', color: { rgb: COLORS.BORDER_HEADER } }
    }
  }

  // Table Headers (Row 4, 0-indexed -> Row 5 in Excel)
  for (let c = 0; c < 15; c++) {
    const colLetter = XLSX.utils.encode_col(c)
    const cellRef = `${colLetter}5`
    if (ws1[cellRef]) {
      ws1[cellRef].s = {
        font: { sz: 11, bold: true, color: { rgb: COLORS.WHITE }, name: 'Calibri' },
        fill: { fgColor: { rgb: COLORS.NAVY_PRIMARY } },
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
        border: {
          top: { style: 'thin', color: { rgb: COLORS.BORDER_HEADER } },
          bottom: { style: 'medium', color: { rgb: COLORS.SLATE_900 } },
          left: { style: 'thin', color: { rgb: COLORS.BORDER_HEADER } },
          right: { style: 'thin', color: { rgb: COLORS.BORDER_HEADER } }
        }
      }
    }
  }

  // Data Rows (Row 5 to 5 + validTickets.length - 1)
  for (let r = 0; r < validTickets.length; r++) {
    const rowIndex = r + 6 // 1-indexed Excel row
    const isEven = r % 2 === 0
    const rowFill = { fgColor: { rgb: isEven ? COLORS.WHITE : COLORS.SLATE_50 } }
    const cellBorder = {
      top: { style: 'thin', color: { rgb: COLORS.BORDER_DATA } },
      bottom: { style: 'thin', color: { rgb: COLORS.BORDER_DATA } },
      left: { style: 'thin', color: { rgb: COLORS.BORDER_DATA } },
      right: { style: 'thin', color: { rgb: COLORS.BORDER_DATA } }
    }

    for (let c = 0; c < 15; c++) {
      const colLetter = XLSX.utils.encode_col(c)
      const cellRef = `${colLetter}${rowIndex}`
      const cell = ws1[cellRef]
      if (!cell) continue

      let horizontal = 'left'
      let isBold = false
      let fontColor = COLORS.SLATE_900
      let underline = false

      // Alignments & Styles per Column
      if (c === 0 || c === 1 || c === 6 || c === 7 || c === 9 || c === 11) {
        horizontal = 'center'
      } else if (c === 13) {
        horizontal = 'right'
      }

      if (c === 6) {
        isBold = true // Ticket Number bold
      }

      if (c === 10 && cell.l) {
        horizontal = 'center'
        isBold = true
        underline = true
        fontColor = COLORS.LINK_BLUE
      }

      cell.s = {
        font: { sz: 10, bold: isBold, underline, color: { rgb: fontColor }, name: 'Calibri' },
        fill: rowFill,
        alignment: { horizontal, vertical: 'center', wrapText: c === 14 },
        border: cellBorder
      }
    }
  }

  // Summary Row Styling (Row summaryRowIndex1 + 1)
  const sumExcelRow1 = summaryRowIndex1 + 1
  for (let c = 0; c < 15; c++) {
    const colLetter = XLSX.utils.encode_col(c)
    const cellRef = `${colLetter}${sumExcelRow1}`
    if (!ws1[cellRef]) {
      ws1[cellRef] = { v: '', t: 's' }
    }
    const isTotalNumberCell = c === 13
    const isTotalTitleCell = c === 0

    ws1[cellRef].s = {
      font: { sz: 11, bold: true, color: { rgb: COLORS.SLATE_900 }, name: 'Calibri' },
      fill: { fgColor: { rgb: COLORS.ICE_BLUE } },
      alignment: {
        horizontal: isTotalTitleCell ? 'right' : (isTotalNumberCell ? 'right' : 'center'),
        vertical: 'center'
      },
      border: {
        top: { style: 'thin', color: { rgb: COLORS.BORDER_TOTAL } },
        bottom: { style: 'double', color: { rgb: COLORS.BORDER_TOTAL } },
        left: { style: 'thin', color: { rgb: COLORS.BORDER_DATA } },
        right: { style: 'thin', color: { rgb: COLORS.BORDER_DATA } }
      }
    }
  }

  XLSX.utils.book_append_sheet(wb, ws1, 'รายงานการแจ้งซ่อม')

  // -------------------------------------------------------------
  // 2. SHEET 2: สรุปผลการประเมินช่าง (Technician Performance Summary)
  // -------------------------------------------------------------
  const sheet2Headers = [
    'ลำดับ',
    'ชื่อช่างเทคนิค',
    'แผนก / ฝ่าย',
    'งานทั้งหมด (งาน)',
    'งานที่สำเร็จ (งาน)',
    'ตรงตามเวลา SLA (งาน)',
    'อัตราตรงเวลา (SLA %)',
    'คะแนนประเมินเฉลี่ย',
    'รายการอะไหล่ที่ใช้รวม',
    'ค่าใช้จ่ายรวม (บาท)'
  ]

  const sheet2Data = []

  // Row 0: Organization Banner
  sheet2Data.push(['มหาวิทยาลัยแม่ฟ้าหลวง - MFU SmartFix System', ...new Array(9).fill('')])
  // Row 1: Subtitle
  sheet2Data.push(['รายงานสรุปผลการปฏิบัติงานและการประเมินช่างเทคนิค (Technician Performance Summary)', ...new Array(9).fill('')])
  // Row 2: Metadata Info
  sheet2Data.push([
    isEn
      ? `Date Range: ${dateRangeText}   |   Department/Division: ${deptText}   |   Exported: ${printTimestamp}   |   Total Technicians: ${evalItems.length}`
      : `ช่วงวันที่: ${dateRangeText}   |   แผนก/ฝ่าย: ${deptText}   |   ออกรายงานเมื่อ: ${printTimestamp}   |   ช่างเทคนิคทั้งหมด: ${evalItems.length} ท่าน`,
    ...new Array(9).fill('')
  ])
  // Row 3: Blank Spacer
  sheet2Data.push(new Array(10).fill(''))
  // Row 4: Column Headers
  sheet2Data.push(sheet2Headers)

  let sumTotalJobs = 0
  let sumCompletedJobs = 0
  let sumOnTimeJobs = 0
  let grandTotalCost = 0
  let ratingWeightedSum = 0
  let ratingWeightedCount = 0

  evalItems.forEach((item, idx) => {
    const indexNo = idx + 1
    const totalJobs = Number(item.totalJobs) || 0
    const completedJobs = Number(item.completedJobs) || 0
    const onTimeJobs = Number(item.onTimeJobs) || 0
    const slaMetPercent = Number(item.slaMetPercent) || 0
    const avgRating = Number(item.avgRating) || 0

    sumTotalJobs += totalJobs
    sumCompletedJobs += completedJobs
    sumOnTimeJobs += onTimeJobs
    if (avgRating > 0) {
      ratingWeightedSum += avgRating * (completedJobs || 1)
      ratingWeightedCount += (completedJobs || 1)
    }

    // Calculate spare parts and cost for this technician
    let techTotalCost = 0
    const sparePartsList = []
    const techIdStr = String(item._id || '')

    validTickets.forEach(tkt => {
      const assignments = tkt?.supervisorPart?.assignments
      let matchedAssignment = null
      if (Array.isArray(assignments)) {
        matchedAssignment = assignments.find(a => String(a.assignee?._id || a.assignee || '') === techIdStr)
      } else if (String(tkt?.supervisorPart?.assignedTechnician?._id || '') === techIdStr) {
        matchedAssignment = { percentage: 100 }
      }

      if (matchedAssignment) {
        const pct = (Number(matchedAssignment.percentage) || 100) / 100
        const techPart = tkt.technicianPart || {}
        let tktCost = 0
        if (Array.isArray(techPart.parts) && techPart.parts.length) {
          tktCost = techPart.parts.reduce((sum, p) => {
            if (p.name) sparePartsList.push(p.name)
            return sum + (Number(p.unitPrice) || 0) * (Number(p.quantity) || 1)
          }, 0)
        } else if (Number(techPart.cost) > 0) {
          tktCost = Number(techPart.cost)
          if (techPart.spareParts && techPart.spareParts !== '-') {
            sparePartsList.push(techPart.spareParts)
          }
        }
        techTotalCost += tktCost * pct
      }
    })

    grandTotalCost += techTotalCost
    const partsUsedStr = sparePartsList.length ? Array.from(new Set(sparePartsList)).join(', ') : '-'
    const ratingDisplay = avgRating > 0 ? `${avgRating.toFixed(1)} / 5.0` : '-'

    sheet2Data.push([
      indexNo,
      item.name || 'ช่างเทคนิค',
      item.department || '-',
      totalJobs,
      completedJobs,
      onTimeJobs,
      `${slaMetPercent}%`,
      ratingDisplay,
      partsUsedStr,
      techTotalCost > 0 ? techTotalCost.toLocaleString('th-TH') : '0'
    ])
  })

  // Summary Row at bottom
  const summaryRowIndex2 = sheet2Data.length
  const overallSlaPercent = sumCompletedJobs > 0 ? Math.round((sumOnTimeJobs / sumCompletedJobs) * 100) : 0
  const overallAvgRating = ratingWeightedCount > 0 ? (ratingWeightedSum / ratingWeightedCount) : 0

  sheet2Data.push([
    `ยอดรวมและค่าเฉลี่ยทั้งหมด (${evalItems.length} ท่าน)`,
    '',
    '',
    sumTotalJobs,
    sumCompletedJobs,
    sumOnTimeJobs,
    `${overallSlaPercent}%`,
    overallAvgRating > 0 ? `${overallAvgRating.toFixed(1)} / 5.0` : '-',
    '-',
    `${grandTotalCost.toLocaleString('th-TH')} บาท`
  ])

  const ws2 = XLSX.utils.aoa_to_sheet(sheet2Data)

  // Merged ranges for Sheet 2
  ws2['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 9 } }, // Title
    { s: { r: 1, c: 0 }, e: { r: 1, c: 9 } }, // Subtitle
    { s: { r: 2, c: 0 }, e: { r: 2, c: 9 } }, // Metadata
    { s: { r: summaryRowIndex2, c: 0 }, e: { r: summaryRowIndex2, c: 2 } } // Summary title
  ]

  // Column widths for Sheet 2
  ws2['!cols'] = [
    { wch: 8 },  // ลำดับ
    { wch: 26 }, // ชื่อช่างเทคนิค
    { wch: 26 }, // แผนก/ฝ่าย
    { wch: 16 }, // งานทั้งหมด
    { wch: 16 }, // งานที่สำเร็จ
    { wch: 18 }, // ตรงตามเวลา SLA
    { wch: 18 }, // อัตราตรงเวลา (SLA %)
    { wch: 22 }, // คะแนนประเมินเฉลี่ย
    { wch: 35 }, // รายการอะไหล่ที่ใช้รวม
    { wch: 20 }  // ค่าใช้จ่ายรวม
  ]

  // Row heights for Sheet 2
  const rowHeights2 = [
    { hpt: 32 }, // Title
    { hpt: 24 }, // Subtitle
    { hpt: 22 }, // Metadata
    { hpt: 8 },  // Spacer
    { hpt: 30 }  // Table Headers
  ]
  for (let i = 0; i < evalItems.length; i++) {
    rowHeights2.push({ hpt: 24 })
  }
  rowHeights2.push({ hpt: 28 }) // Summary Row
  ws2['!rows'] = rowHeights2

  // Freeze pane below Table Header (Row 5)
  ws2['!views'] = [{ xSplit: 0, ySplit: 5, topLeftCell: 'A6', activeCell: 'A6', state: 'frozen' }]

  // -------------------------------------------------------------
  // Apply Cell Styling for Sheet 2
  // -------------------------------------------------------------
  // Title Cell (A1)
  ws2['A1'].s = {
    font: { sz: 14, bold: true, color: { rgb: COLORS.WHITE }, name: 'Calibri' },
    fill: { fgColor: { rgb: COLORS.TEAL_HEADER } },
    alignment: { horizontal: 'center', vertical: 'center' }
  }

  // Subtitle Cell (A2)
  ws2['A2'].s = {
    font: { sz: 11, bold: true, color: { rgb: COLORS.WHITE }, name: 'Calibri' },
    fill: { fgColor: { rgb: COLORS.NAVY_SUBHEADER } },
    alignment: { horizontal: 'center', vertical: 'center' }
  }

  // Metadata Cell (A3)
  ws2['A3'].s = {
    font: { sz: 10, bold: false, color: { rgb: COLORS.SLATE_700 }, name: 'Calibri' },
    fill: { fgColor: { rgb: COLORS.SLATE_100 } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: {
      bottom: { style: 'thin', color: { rgb: COLORS.BORDER_HEADER } }
    }
  }

  // Table Headers (Row 4, 0-indexed -> Row 5 in Excel)
  for (let c = 0; c < 10; c++) {
    const colLetter = XLSX.utils.encode_col(c)
    const cellRef = `${colLetter}5`
    if (ws2[cellRef]) {
      ws2[cellRef].s = {
        font: { sz: 11, bold: true, color: { rgb: COLORS.WHITE }, name: 'Calibri' },
        fill: { fgColor: { rgb: COLORS.TEAL_HEADER } },
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
        border: {
          top: { style: 'thin', color: { rgb: COLORS.BORDER_HEADER } },
          bottom: { style: 'medium', color: { rgb: COLORS.SLATE_900 } },
          left: { style: 'thin', color: { rgb: COLORS.BORDER_HEADER } },
          right: { style: 'thin', color: { rgb: COLORS.BORDER_HEADER } }
        }
      }
    }
  }

  // Data Rows
  for (let r = 0; r < evalItems.length; r++) {
    const rowIndex = r + 6
    const isEven = r % 2 === 0
    const rowFill = { fgColor: { rgb: isEven ? COLORS.WHITE : COLORS.SLATE_50 } }
    const cellBorder = {
      top: { style: 'thin', color: { rgb: COLORS.BORDER_DATA } },
      bottom: { style: 'thin', color: { rgb: COLORS.BORDER_DATA } },
      left: { style: 'thin', color: { rgb: COLORS.BORDER_DATA } },
      right: { style: 'thin', color: { rgb: COLORS.BORDER_DATA } }
    }

    const item = evalItems[r]
    const slaPercent = Number(item.slaMetPercent) || 0

    for (let c = 0; c < 10; c++) {
      const colLetter = XLSX.utils.encode_col(c)
      const cellRef = `${colLetter}${rowIndex}`
      const cell = ws2[cellRef]
      if (!cell) continue

      let horizontal = 'left'
      let isBold = false
      let fontColor = COLORS.SLATE_900

      if (c === 0 || c === 3 || c === 4 || c === 5 || c === 6 || c === 7) {
        horizontal = 'center'
      } else if (c === 9) {
        horizontal = 'right'
      }

      if (c === 1) {
        isBold = true // Tech name bold
      }

      if (c === 6) {
        isBold = true
        fontColor = slaPercent >= 80 ? COLORS.GREEN_SLA : COLORS.AMBER_SLA
      }

      if (c === 7 && cell.v !== '-') {
        isBold = true
      }

      cell.s = {
        font: { sz: 10, bold: isBold, color: { rgb: fontColor }, name: 'Calibri' },
        fill: rowFill,
        alignment: { horizontal, vertical: 'center', wrapText: c === 8 },
        border: cellBorder
      }
    }
  }

  // Summary Row Styling for Sheet 2
  const sumExcelRow2 = summaryRowIndex2 + 1
  for (let c = 0; c < 10; c++) {
    const colLetter = XLSX.utils.encode_col(c)
    const cellRef = `${colLetter}${sumExcelRow2}`
    if (!ws2[cellRef]) {
      ws2[cellRef] = { v: '', t: 's' }
    }
    const isTotalNumberCell = c === 9
    const isTotalTitleCell = c === 0

    ws2[cellRef].s = {
      font: { sz: 11, bold: true, color: { rgb: COLORS.SLATE_900 }, name: 'Calibri' },
      fill: { fgColor: { rgb: COLORS.MINT_LIGHT } },
      alignment: {
        horizontal: isTotalTitleCell ? 'right' : (isTotalNumberCell ? 'right' : 'center'),
        vertical: 'center'
      },
      border: {
        top: { style: 'thin', color: { rgb: COLORS.BORDER_TOTAL_TEAL } },
        bottom: { style: 'double', color: { rgb: COLORS.BORDER_TOTAL_TEAL } },
        left: { style: 'thin', color: { rgb: COLORS.BORDER_DATA } },
        right: { style: 'thin', color: { rgb: COLORS.BORDER_DATA } }
      }
    }
  }

  XLSX.utils.book_append_sheet(wb, ws2, 'สรุปผลการประเมินช่าง')

  // Export File
  const filename = `SmartFix_Report_${today}.xlsx`
  XLSX.writeFile(wb, filename)
  return filename
}
